import React, { useState } from "react";
import { Box, Paper, TextField, Button, Stack, MenuItem, Switch, FormControlLabel, Alert } from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { integrationService } from "../../api/services/automationService";
import { PageHeader } from "../../components/Common";
import DataTable from "../../components/DataTable";

const PROVIDERS = ["ldap", "azure_ad", "google", "microsoft", "slack", "teams"];

export default function IntegrationsPage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [provider, setProvider] = useState(PROVIDERS[0]);
  const [configJson, setConfigJson] = useState("{}");
  const [isEnabled, setIsEnabled] = useState(false);

  const { data: configs, isLoading } = useQuery({ queryKey: ["integrations"], queryFn: integrationService.list });

  const saveMutation = useMutation({
    mutationFn: () => {
      let parsed;
      try {
        parsed = JSON.parse(configJson);
      } catch {
        throw new Error("Config must be valid JSON");
      }
      return integrationService.setConfig({ provider, config: parsed, is_enabled: isEnabled });
    },
    onSuccess: () => {
      enqueueSnackbar("Integration config saved", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
    },
    onError: (err) => enqueueSnackbar(err.message || err.response?.data?.detail || "Save failed", { variant: "error" }),
  });

  const columns = [
    { key: "provider", label: "Provider" },
    { key: "is_enabled", label: "Enabled", render: (r) => (r.is_enabled ? "Yes" : "No") },
    { key: "config", label: "Config keys", render: (r) => Object.keys(r.config || {}).join(", ") || "—" },
  ];

  return (
    <Box>
      <PageHeader title="Integrations" subtitle="LDAP, SSO, and chat provider configuration" />

      <Alert severity="info" sx={{ mb: 2 }}>
        This is configuration storage only — there's no live LDAP bind or OAuth token exchange behind it yet.
        Real SSO/chat integration requires provider app credentials to be wired in on the backend; see the
        backend README's Phase 4 notes for exactly what's scaffolding vs. working.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Stack spacing={2}>
          <TextField select label="Provider" value={provider} onChange={(e) => setProvider(e.target.value)}>
            {PROVIDERS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
          <TextField
            label="Config (JSON)" value={configJson} onChange={(e) => setConfigJson(e.target.value)}
            multiline minRows={3} fullWidth
            helperText='e.g. {"client_id": "...", "tenant": "..."}'
          />
          <FormControlLabel control={<Switch checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />} label="Enabled" />
          <Box>
            <Button variant="contained" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>Save Config</Button>
          </Box>
        </Stack>
      </Paper>

      <DataTable columns={columns} rows={configs} loading={isLoading} emptyMessage="No integrations configured" />
    </Box>
  );
}
